import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { fetchSeriesMetrics, SeriesMetricsData, SeriesIndicator } from "../api";
import { shortPeriodLabel } from "../utils/date";

const SURVEY_SERIES = ["银行家问卷调查", "企业家问卷调查", "城镇储户问卷调查"] as const;
type SurveySeriesName = typeof SURVEY_SERIES[number];

const SURVEY_KEY_INDICATORS: Record<SurveySeriesName, string[]> = {
  "银行家问卷调查": [
    "银行家宏观经济热度指数",
    "贷款总体需求指数",
    "银行业景气指数",
    "货币政策感受指数",
    "银行盈利指数",
    "小微企业贷款需求指数",
    "制造业贷款需求指数",
    "房地产企业贷款需求指数",
  ],
  "企业家问卷调查": [
    "企业家宏观经济热度指数",
    "经营景气指数",
    "盈利指数",
    "国内订单指数",
    "出口订单指数",
    "资金周转指数",
    "销货款回笼指数",
  ],
  "城镇储户问卷调查": [
    "居民收入感受指数",
    "就业感受指数",
    "就业预期指数",
    "消费意愿指数",
    "储蓄意愿指数",
    "物价预期指数",
    "投资意愿指数",
  ],
};

const SURVEY_DESCRIPTIONS: Record<SurveySeriesName, string> = {
  "银行家问卷调查": "央行季度调查，反映银行信贷供需及货币政策感受。",
  "企业家问卷调查": "衡量企业经营景气度、盈利预期与融资状况。",
  "城镇储户问卷调查": "反映居民消费意愿、储蓄偏好与收入预期。",
};

const HIGHER_IS_WORSE: string[] = ["不良贷款率预期指数", "储蓄意愿指数", "物价预期指数"];

function deltaColor(delta: number | null, metricName: string): string {
  if (delta === null) return "text-ink-faint";
  const higherIsBetter = !HIGHER_IS_WORSE.some((k) => metricName.includes(k));
  if (delta > 0) return higherIsBetter ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400";
  if (delta < 0) return higherIsBetter ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400";
  return "text-ink-faint";
}

function formatDelta(delta: number | null): string {
  if (delta === null) return "—";
  return `${delta > 0 ? "+" : ""}${delta.toFixed(1)}`;
}

interface HistoryChartProps {
  name: string;
  indicator: SeriesIndicator;
}

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ payload: { period: string; raw: string } }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border-subtle bg-surface-raised p-2 text-xs shadow-card">
      <p className="mb-0.5 font-medium text-ink-secondary">{d.period || label}</p>
      <p className="font-semibold text-accent-dark dark:text-accent">{d.raw}</p>
    </div>
  );
};

function HistoryChart({ name, indicator }: HistoryChartProps) {
  const periods = Object.keys(indicator.values)
    .filter((p) => /^\d{4}Q\d$/.test(p) && parseInt(p) >= 2019)
    .sort();

  const data = periods.map((p) => {
    const entry = indicator.values[p];
    return {
      period: p,
      displayPeriod: shortPeriodLabel(p),
      value: entry?.num ?? null,
      raw: entry?.raw ?? "",
    };
  });

  const hasData = data.filter((d) => d.value !== null).length >= 2;

  if (!hasData) {
    return <p className="py-4 text-center text-[11px] text-ink-faint">历史数据不足（至少需要 2 个季度）</p>;
  }

  const unit = indicator.unit ? `（${indicator.unit}）` : "";

  return (
    <div className="mt-3 rounded-xl border border-border bg-surface px-3 pt-3 pb-1">
      <p className="mb-2 text-[11px] font-medium text-ink-secondary">
        {name} {unit} · 历史趋势
      </p>
      <ResponsiveContainer width="100%" height={164}>
        <LineChart data={data} margin={{ left: -8, right: 8, top: 4, bottom: 18 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
          <XAxis
            dataKey="displayPeriod"
            tick={{ fontSize: 9, fill: "var(--chart-axis)" }}
            angle={-45}
            textAnchor="end"
            tickLine={false}
            axisLine={false}
            interval={0}
            height={42}
          />
          <YAxis
            tick={{ fontSize: 9, fill: "var(--chart-axis)" }}
            tickLine={false}
            axisLine={false}
            width={34}
            domain={["auto", "auto"]}
            tickFormatter={(v: number) => v.toFixed(1)}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#375E8A"
            strokeWidth={2}
            dot={{ r: 2.5, fill: "#375E8A", stroke: "#FBF8F2", strokeWidth: 1.5 }}
            activeDot={{ r: 4.5, fill: "#C9A96E", stroke: "#FBF8F2", strokeWidth: 1.5 }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

interface IndicatorRowProps {
  name: string;
  indicator: SeriesIndicator;
  isExpanded: boolean;
  onToggle: () => void;
}

function IndicatorRow({ name, indicator, isExpanded, onToggle }: IndicatorRowProps) {
  const periods = Object.keys(indicator.values)
    .filter((p) => /^\d{4}Q\d$/.test(p))
    .sort();
  const latest = periods[periods.length - 1];
  const prev = periods[periods.length - 2];

  const latestVal = latest ? indicator.values[latest] : undefined;
  const prevVal = prev ? indicator.values[prev] : undefined;

  const num = latestVal?.num ?? null;
  const prevNum = prevVal?.num ?? null;
  const delta = num !== null && prevNum !== null ? num - prevNum : null;
  const clr = deltaColor(delta, name);
  const hasHistory = periods.length >= 2;

  return (
    <div className="border-b border-border/70 last:border-0">
      <button
        onClick={hasHistory ? onToggle : undefined}
        className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors ${
          hasHistory ? "cursor-pointer hover:bg-surface" : "cursor-default"
        } ${isExpanded ? "bg-surface" : ""}`}
      >
        <div className="min-w-0 flex-1">
          <span className={`block truncate text-[12px] ${isExpanded ? "font-semibold text-ink" : "text-ink-secondary"}`}>
            {name}
          </span>
          {latest && <span className="mt-0.5 block text-[10px] text-ink-faint">{latest}</span>}
        </div>
        <div className="flex shrink-0 items-center gap-1.5 text-right">
          <div>
            <span className="data-num text-[15px] font-semibold text-ink">{latestVal?.raw || "—"}</span>
            {delta !== null && <span className={`ml-1.5 text-[11px] font-medium ${clr}`}>{formatDelta(delta)}</span>}
          </div>
          {hasHistory && <span className={`text-[10px] text-ink-faint transition-transform ${isExpanded ? "rotate-180" : ""}`}>▾</span>}
        </div>
      </button>

      {isExpanded && <HistoryChart name={name} indicator={indicator} />}
    </div>
  );
}

interface SurveyPanelProps {
  seriesName: SurveySeriesName;
  data: SeriesMetricsData | undefined;
  isLoading: boolean;
}

function SurveyPanel({ seriesName, data, isLoading }: SurveyPanelProps) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const keyIndicators = SURVEY_KEY_INDICATORS[seriesName];
  const indicators = data?.indicators ?? {};
  const usedKeys = new Set<string>();
  const matched: Array<{ key: string; indicator: SeriesIndicator }> = [];

  for (const key of keyIndicators) {
    if (indicators[key]) {
      matched.push({ key, indicator: indicators[key] });
      usedKeys.add(key);
    }
  }

  const extras = Object.entries(indicators)
    .filter(([name]) => !usedKeys.has(name))
    .sort((a, b) => Object.keys((b[1] as SeriesIndicator).values).length - Object.keys((a[1] as SeriesIndicator).values).length);

  for (const [key, indicator] of extras) {
    matched.push({ key, indicator });
  }

  const totalIndicators = Object.keys(indicators).length;

  const handleToggle = (key: string) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  return (
    <div className="paper-panel flex flex-col overflow-hidden">
      <div className="border-b border-border px-4 py-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[14px] font-semibold tracking-tight text-ink">{seriesName}</div>
            <div className="mt-1 text-[11px] leading-5 text-ink-secondary">{SURVEY_DESCRIPTIONS[seriesName]}</div>
          </div>
          {totalIndicators > 0 && (
            <span className="rounded-full border border-border bg-surface px-2 py-1 text-[10px] font-mono tabular-nums text-ink-tertiary">
              {totalIndicators} 项
            </span>
          )}
        </div>
        <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-accent-dark/80 dark:text-accent/80">
          点击指标可展开历史趋势
        </div>
      </div>

      <div className="px-2 py-2">
        {isLoading && (
          <div className="space-y-2 px-2 py-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-xl bg-surface-sunken" />
            ))}
          </div>
        )}

        {!isLoading && matched.length === 0 && (
          <div className="py-10 text-center text-[12px] text-ink-faint">暂无指标数据</div>
        )}

        {!isLoading && matched.length > 0 && (
          <div>
            {matched.map(({ key, indicator }) => (
              <IndicatorRow
                key={key}
                name={key}
                indicator={indicator}
                isExpanded={expandedKey === key}
                onToggle={() => handleToggle(key)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function SurveyCard() {
  const { data: allSeries = [], isLoading } = useQuery({
    queryKey: ["series-metrics-surveys"],
    queryFn: () => Promise.all(SURVEY_SERIES.map((s) => fetchSeriesMetrics(s).then((arr: SeriesMetricsData[]) => arr[0]))),
    staleTime: 10 * 60 * 1000,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="section-kicker">Survey Signals</p>
          <h2 className="mt-1 text-xl font-semibold tracking-tight text-ink">问卷调查</h2>
          <p className="mt-1 text-sm text-ink-secondary">央行季度问卷调查的核心景气指数。</p>
        </div>
        <div className="rounded-full border border-border bg-surface px-3 py-1 text-2xs font-mono tabular-nums text-ink-tertiary">
          3 组调查 / 核心指标优先
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        {SURVEY_SERIES.map((seriesName, i) => (
          <SurveyPanel key={seriesName} seriesName={seriesName} data={allSeries[i]} isLoading={isLoading} />
        ))}
      </div>
    </div>
  );
}
